<script setup lang="ts">import { ref as _ref, toRef as _toRef } from 'vue';
const __props_mousePosition = _toRef(__props, 'mousePosition');

import { watch } from "vue";
import { v4 as uuid } from "uuid";
import { useRoute, useRouter } from "vue-router";

import { useMessageStore } from "@/stores/message-store";
import { markDefaults, saveAnnotation } from "@/services/AnnotationService";

import type { PageData } from "@/models/PageData";
import type { MarkBase } from "@/models/MarkBase";
import type { BoundingRectangle } from "@/models/BoundingRectangle";

const MIN_MARK_WIDTH = 20;
const MIN_MARK_HEIGHT = 10;

type DRAG_STATE = "IDLE" | "CREATE";
const emit = defineEmits<{
  (e: "reloadAnnotations"): void;
  (e: "dragEnd"): void;
}>();

withDefaults(defineProps<{
  pageScale: number;
  pageDimensions: PageData;
  pageNumber: number;
  session: string;
  fileReference: string;
  type: string;
  mousePosition: { x: number; y: number } | null;
}>(), {  })

const $route = useRoute();
const $router = useRouter();

let dragState = _ref<DRAG_STATE>("IDLE");
let selected = _ref<MarkBase | null>(null);
let rectangleInit = _ref<BoundingRectangle | null>(null);
let offset = _ref<{ x: number; y: number } | null>(null);

function dragStart(evt: MouseEvent, state: DRAG_STATE) {
  dragState.value = state;
  const m = __props.mousePosition!;

  selected.value = {
    data: { status: "user" },
    ...markDefaults[__props.type],
    pageData: __props.pageDimensions,
    pageNumber: __props.pageNumber,
    uid: uuid(),
    conversation: { comments: [], data: {} },
    creationDateTime: new Date().toISOString(),
    modificationDateTime: new Date().toISOString(),
    borderThickness: 1,
    type: __props.type,
    rectangle: {
      x: m.x,
      y: m.y,
      width: 0,
      height: 0,
    },
  };

  rectangleInit.value = { ...selected.value.rectangle! };

  offset.value = {
    x: m.x - rectangleInit.value.x,
    y: m.y - rectangleInit.value.y,
  };
}

watch((__props_mousePosition), () => {
  if (dragState.value === "IDLE" || !selected.value) return;

  const r = selected.value.rectangle!;
  const rInit = rectangleInit.value!;
  const m = __props.mousePosition!;

  r.x = Math.min(rInit.x, m.x);
  r.y = Math.min(rInit.y, m.y);
  r.width = Math.abs(rInit.x - m.x);
  r.height = Math.abs(rInit.y - m.y);
});

async function dragEnd() {
  if (dragState.value === "IDLE" || !selected.value?.rectangle) return;
  dragState.value = "IDLE";

  if (selected.value.rectangle.width < MIN_MARK_WIDTH || selected.value.rectangle.height < MIN_MARK_HEIGHT) {
    useMessageStore().updateMessage({
      message: `A mark must be at least ${MIN_MARK_WIDTH}px by ${MIN_MARK_HEIGHT}px in size. Your selected area was only ${Math.round(selected.value.rectangle.width)}px by ${Math.round(selected.value.rectangle.height)}px.`,
      type: "warning",
      title: "Could not create annotation",
      autoHide: true,
      showReload: false,
    });
  } else {
    await saveAnnotation({
      fileReference: __props.fileReference,
      markId: selected.value.uid,
      source: "user",
      supportingFileEntryFileReference: "",
      metadata: undefined,
      evidenceThemes: undefined,
      markData: JSON.stringify(selected.value),
      parsedMarkData: selected.value as never,
    });

    emit("reloadAnnotations");
    selectAnnotation(selected.value.uid);
    emit("dragEnd");
  }

  selected.value = null;
}

function selectAnnotation(markId: string) {
  const { brand, casename, casefile } = $route.params;

  $router.push({
    name: "casefileannotation",
    params: { brand, casename, casefile, annotation: markId },
  });
}
</script>
<template>
  <g>
    <svg
      v-if="selected?.rectangle"
      :height="selected.pageData.height"
      :width="selected.pageData.width"
      :viewBox="`0 0 ${selected.pageData.width} ${selected.pageData.height}`"
    >
      <rect
        :x="selected.rectangle.x"
        :y="selected.rectangle.y"
        :width="selected.rectangle.width"
        :height="selected.rectangle.height"
        :style="`fill: ${selected.fillColor}; stroke: ${
          selected.borderColor
        }; stroke-width: ${selected.borderThickness}; opacity: ${
          selected.opacity && selected.opacity <= 1 ? selected.opacity : 0.4
        }; cursor: move;`"
      ></rect>
    </svg>
    <rect
      :x="0"
      :y="0"
      :width="pageDimensions.width"
      :height="pageDimensions.height"
      :style="`opacity: 0; cursor: crosshair;`"
      @mousedown="dragStart($event, 'CREATE')"
      @mouseup="dragEnd"
    />
  </g>
</template>
